import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import {
  CollapseDownIcon,
  CollapseUpIcon,
  DeleteIcon,
  DragAndOrderIcon,
} from "../../../../assets/icons/index";
import Constant from "../../../../utils/constant";
import { ILiftOffDiagnosticTestQuestionDTO } from "../../../../model/interface/liftOffDiagnosticTest";
import { IStandardGroupStandardDTO } from "../../../../model/interface/standardGroup";

interface IStandardCardSortableItemProps {
  item: ILiftOffDiagnosticTestQuestionDTO;
  index: number;
  toggle: number | undefined;
  handleShowGroupedStandards: (
    standardGroupId: number,
    index: number,
    type: string
  ) => void;
  confirmDeleteQuestion: (id: number) => void;
  groupedStandards: Array<IStandardGroupStandardDTO>;
}

const StandardCardSortableItem: React.FC<IStandardCardSortableItemProps> = ({
  item,
  index,
  toggle,
  handleShowGroupedStandards,
  confirmDeleteQuestion,
  groupedStandards,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.liftOffDiagTestQuestionId.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Separate drag handle styles
  const dragHandleStyle = {
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <Disclosure>
      <tr ref={setNodeRef} style={style} className="bg-white">
        <td className="px-3 py-2">
          <div
            {...attributes}
            {...listeners}
            style={dragHandleStyle}
            className="inline-flex items-center justify-center"
          >
            <DragAndOrderIcon className="w-6 h-6" />
          </div>
        </td>
        <td>
          {item.isStandardGroup && (
            <div>
              <DisclosureButton>
                {index !== toggle && (
                  <CollapseDownIcon
                    key={index}
                    className={`w-5 h-5 text-gray-500`}
                    onClick={() => {
                      handleShowGroupedStandards(
                        item.standardReferenceId,
                        index,
                        "show"
                      );
                    }}
                  />
                )}

                {index === toggle && (
                  <CollapseUpIcon
                    key={index}
                    className={`w-5 h-5 text-gray-500`}
                    onClick={() => {
                      handleShowGroupedStandards(
                        item.standardReferenceId,
                        index,
                        "hide"
                      );
                    }}
                  />
                )}
              </DisclosureButton>
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
          {item.orderNumber}
        </td>
        <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
          {item.standardCode}
        </td>
        <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
          {item.isStandardGroup == false && (
            <Link
              to={Constant.EDITSTANDARD.replace(
                ":id",
                item.standardReferenceId
                  ? item.standardReferenceId.toString()
                  : ""
              )}
              className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
            >
              {item.standard}
            </Link>
          )}
          {item.isStandardGroup == true && (
            <Link
              className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
              to={Constant.STANDARDGROUPEDIT.replace(
                ":id",
                item.standardReferenceId.toString()
              )}
            >
              {item.standard}
            </Link>
          )}
        </td>
        <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
          {item.grade}
        </td>
        <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
          <Link
            to={Constant.MANAGEQUESTION.replace(
              ":id",
              item.questionId1 ? item.questionId1.toString() : ""
            )}
            className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
          >
            {item.questionId1}
          </Link>
        </td>
        <td className="px-3 py-2 text-sm text-left font-normal text-gray-500">
          <Link
            to={Constant.MANAGEQUESTION.replace(
              ":id",
              item.questionId2 ? item.questionId2.toString() : ""
            )}
            className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
          >
            {item.questionId2}
          </Link>
        </td>
        <td className="px-3 py-2 cursor-pointer font-medium text-indigo-600 hover:text-red-500 transaction ease-in-out duration-300">
          <DeleteIcon
            className="w-6 h-6"
            onClick={() => {
              confirmDeleteQuestion(item.liftOffDiagTestQuestionId);
            }}
          />
        </td>
      </tr>
      <tr>
        <td colSpan={8}>
          <div
            className={`${
              index !== toggle ? "hidden" : ""
            }  px-4 pt-4 pb-2 text-sm text-gray-500`}
          >
            <div className="shadow border rounded-lg px-3 py-3">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th
                      scope="col"
                      className="inline-flex items-center gap-2 px-3 py-2 pl-6 font-normal text-gray-500 text-xs uppercase text-left tracking-wider"
                    >
                      <span className="flex gap-2">Standard Id</span>
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-2 pl-6 font-normal text-xs uppercase text-left text-gray-500 tracking-wider"
                    >
                      <span className="flex gap-2">Code</span>
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-2 pl-6 font-normal text-xs uppercase text-left text-gray-500 tracking-wider"
                    >
                      <span className="flex gap-2">Name</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {groupedStandards.map((standard, index) => (
                    <tr key={index}>
                      <td className="font-medium pl-6">
                        <Link
                          to={Constant.EDITSTANDARD.replace(
                            ":id",
                            standard.standardId
                              ? standard.standardId.toString()
                              : ""
                          )}
                          className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                        >
                          {standard.standardId}
                        </Link>
                      </td>
                      <td className="px-3 py-2  pl-6 whitespace-nowrap text-sm text-left font-normal text-gray-500">
                        {standard.standardCode}
                      </td>
                      <td className="px-3 py-2  text-sm text-center font-normal text-gray-500">
                        {standard.standardName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    </Disclosure>
  );
};

export default StandardCardSortableItem;
